import { Link, createFileRoute } from "@tanstack/react-router";
import NavBar from "#/components/navbar/navbar";
import Footer from "#/components/footer/footer";
import Mdx from "#/components/MdxRemote";
import Author from "#/components/author";
import { organization } from "#/lib/jsonLdSchema";
import { toAppPath } from "#/lib/router-links";
import { metadataToHead } from "#/lib/seo";

export default function Page() {
  return (
    <>
      <header>
        <NavBar />
      </header>
      <main id="content" className="mx-auto mt-24 max-w-3xl space-y-10">
        <h1 className="text-center text-5xl">About Roamdoc</h1>

        <section className="mx-8 space-y-5">
          <Mdx source={content} />
        </section>

        <Author />

        <section className="mx-8 space-y-5">
          <h2 className="mt-10 text-3xl">Contact Us</h2>

          <p>
            Looking to{" "}
            <Link {...toAppPath("/recruit-anesthesia-providers")}>
              recruit providers in anesthesiology
            </Link>{" "}
            who excel in their field? Contact us today and discover how our
            specialized recruiting services can connect you with top-tier
            anesthesiology professionals tailored to your specific needs.
          </p>

          <Link
            {...toAppPath("/contact-roamdoc")}
            className="rounded border border-blue-500 bg-transparent px-4 py-2 font-semibold text-blue-700 no-underline hover:border-transparent hover:bg-blue-500 hover:text-white "
          >
            Contact Roamdoc
          </Link>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={addJsonLd()}
      />
    </>
  );
}

const content = `
Roamdoc is a job search engine for physicians in anesthesiology.

Your premier destination for specialized job listings in the medical field, focusing exclusively on Anesthesiologists and CRNAs. At Roamdoc, we understand the critical importance of these roles in the healthcare system and strive to connect top talent with leading healthcare providers.

## Our Story
Roamdoc was founded by Frank Melo, MD, a anesthesiologist with over 35 years of experience. Dr. Melo has worked in a variety of settings, including private practice and locum tenens. He has also served as a medical director and has been involved in the recruitment and hiring of anesthesiologists and CRNAs.

## Our Mission
Our mission is simple yet profound: to facilitate perfect matches between healthcare professionals and medical facilities. We believe that every anesthesiologist and CRNA has a unique set of skills and aspirations, and our goal is to align these with the needs and visions of healthcare institutions. This synergy not only enhances career satisfaction but also elevates patient care standards.

## Why Choose Us
At Roamdoc, it's not just about job listings. It's about building connections within the medical community. We facilitate networking opportunities, allowing professionals to share insights, experiences, and forge long-term relationships.

## Our Commitment
- **Dedicated Service**: Our team is committed to providing exceptional service, ensuring a seamless and efficient job search experience.
- **Expertise**: With a focus on Anesthesiology and CRNA positions, our expertise is unmatched in these fields.
- **Confidentiality**: We maintain the highest standards of confidentiality and professionalism in all our interactions.
- **Support**: From job alerts to personalized recruiter assistance, we are here to support you at every step of your career journey.

## Our Vision
Our vision is to become the leading job search engine for Anesthesiologists and CRNAs, providing a comprehensive range of opportunities for these professionals.

## Meet the Team
`;

export const metadata = {
  title: "About Roamdoc • Job search engine & recruiting for physicians",
  description:
    "About Roamdoc, we specialize in connecting top talent with premier opportunities in the medical field.",
  icons: [{ rel: "icon", url: "/favicon.ico" }],
  openGraph: {
    images: ["https://www.roamdoc.com/og-image.png"],
  },
  alternates: {
    canonical: `https://www.roamdoc.com/about-roamdoc`,
  },
};

function addJsonLd() {
  return {
    __html: `{
      "@context": "https://schema.org/",
      "@type": "AboutPage",
      "url": "https://www.roamdoc.com/about-roamdoc",
      "inLanguage": "en-US",
      "mainEntityOfPage": ${JSON.stringify(organization())},
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Customer Support",
        "url": "https://www.roamdoc.com/contact-roamdoc",
        "areaServed": "US",
        "availableLanguage": ["English", "Spanish"]
      }
    }`,
  };
}

export const Route = createFileRoute("/about-roamdoc/")({
  component: Page,
  head: () => metadataToHead(metadata),
});
